import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, X, Home, Play, Star, CreditCard, ArrowRight, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    LANDING_BLOG_NAV_ITEM,
    LANDING_SECTION_NAV_ITEMS,
    scrollToLandingSection,
} from '../lib/landingNav';

interface HamburgerMenuProps {
    className?: string;
}

const SECTION_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
    home: Home,
    demo: Play,
    features: Star,
    pricing: CreditCard,
    cta: ArrowRight,
};

const HamburgerMenu: React.FC<HamburgerMenuProps> = ({ className = '' }) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    const handleNavigation = (sectionId: string) => {
        scrollToLandingSection(sectionId);
        setIsOpen(false);
    };

    const handleBackdropClick = () => {
        setIsOpen(false);
    };

    return (
        <>
            <div className={className}>
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsOpen(!isOpen)}
                    className="h-11 w-11 rounded-xl hover:bg-accent/50 transition-all duration-200"
                    aria-label={t('navigation.menu')}
                >
                    <div className="relative w-5 h-5">
                        <Menu className={`absolute inset-0 transition-all duration-300 ${isOpen ? 'opacity-0 rotate-180' : 'opacity-100 rotate-0'}`} />
                        <X className={`absolute inset-0 transition-all duration-300 ${isOpen ? 'opacity-100 rotate-0' : 'opacity-0 -rotate-180'}`} />
                    </div>
                </Button>
            </div>

            {isOpen && (
                <div
                    className="fixed inset-0 z-40 bg-black/40 transition-opacity duration-300"
                    onClick={handleBackdropClick}
                    aria-hidden="true"
                />
            )}

            <div
                ref={menuRef}
                className={`fixed top-16 right-4 z-50 transition-all duration-300 ease-out ${isOpen ? 'translate-x-0 opacity-100 scale-100' : 'translate-x-full opacity-0 scale-95'
                    }`}
            >
                <div className="bg-background border border-border rounded-2xl shadow-2xl min-w-[260px] max-w-[280px] overflow-hidden">
                    <div className="py-2">
                        {LANDING_SECTION_NAV_ITEMS.map((item, index) => {
                            const IconComponent = SECTION_ICONS[item.key] ?? Home;
                            return (
                                <button
                                    key={item.key}
                                    type="button"
                                    onClick={() => item.sectionId && handleNavigation(item.sectionId)}
                                    className="w-full text-left px-4 py-3 hover:bg-accent/50 transition-all duration-200 text-foreground font-medium min-h-[48px] flex items-center gap-3 group"
                                    style={{ animationDelay: `${index * 50}ms` }}
                                >
                                    <IconComponent className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                                    <span className="flex-1">{t(item.labelKey)}</span>
                                </button>
                            );
                        })}

                        <div className="border-t border-border/50 mx-4 my-2" />
                        <a
                            href={LANDING_BLOG_NAV_ITEM.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setIsOpen(false)}
                            className="w-full text-left px-4 py-3 hover:bg-accent/50 transition-all duration-200 text-foreground font-medium min-h-[48px] flex items-center gap-3 group"
                            style={{ animationDelay: `${LANDING_SECTION_NAV_ITEMS.length * 50}ms` }}
                        >
                            <BookOpen className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                            <span className="flex-1">{t(LANDING_BLOG_NAV_ITEM.labelKey)}</span>
                        </a>
                    </div>
                </div>
            </div>
        </>
    );
};

export default HamburgerMenu;
